import type { ReactNode } from 'react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';

/**
 * Pill/Chip de filtro — ÚNICO estilo para toda la app (Proyectos, Cotización,
 * APUs, etc.). Activo = acento (var(--accent)). No crear pills ad-hoc por vista.
 * Forma del hub del cliente: rectángulo de radio 8px (--radius-control) con borde fino,
 * no píldora.
 */
const useStyles = makeStyles({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    height: '32px',
    padding: '0 13px',
    borderRadius: 'var(--radius-control)',
    border: '1px solid var(--control-border)',
    background: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transitionProperty: 'color, background, border-color',
    transitionDuration: '120ms',
    ':hover': { background: 'var(--bg-hover)', border: '1px solid var(--control-border-hover)', color: 'var(--text-primary)' },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '2px' },
  },
  active: {
    background: 'var(--accent-weak)',
    border: '1px solid var(--accent)',
    color: 'var(--accent)',
    ':hover': { background: 'var(--accent-weak)', border: '1px solid var(--accent)', color: 'var(--accent)' },
  },
  dot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  count: { fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: 700, opacity: 0.85 },
});

export interface ChipProps {
  label: ReactNode;
  count?: number | string | null;
  /** Color del punto (estado/confianza). Omitir para no mostrar punto. */
  dotColor?: string;
  icon?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  title?: string;
}

export function Chip({ label, count, dotColor, icon, active, onClick, title }: ChipProps) {
  const styles = useStyles();
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={mergeClasses(styles.chip, active && styles.active)}
      aria-pressed={active}
    >
      {dotColor && <span className={styles.dot} style={{ background: dotColor }} />}
      {icon}
      {label}
      {count != null && count !== '' && <span className={styles.count}>{count}</span>}
    </button>
  );
}
